'use client';

import UiIcon from '@/components/ui/UiIcon';
import { useEffect, useRef } from 'react';
import Link from 'next/link';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';

type StaffMember = {
  id: number;
  username: string;
  display_name?: string | null;
  avatar_display_url?: string | null;
  position?: string | null;
  organization?: string | null;
  bio?: string | null;
  telegram_username?: string | null;
  website?: string | null;
  role?: { name: string; color?: string | null; priority?: number | null } | null;
  work_time?: string | null;
  work_since?: string | null;
  articles_count?: number;
  projects_count?: number;
  projects?: Array<{ id: number; name: string; role?: string | null }>;
};

interface StaffModalProps {
  open: boolean;
  onClose: () => void;
  staff: StaffMember[];
}

export default function StaffModal({ open, onClose, staff }: StaffModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Закрытие по Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Блокировка скролла body при открытом модальном окне
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Оверлей */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Модальное окно */}
      <div
        ref={dialogRef}
        className="relative z-10 w-full max-w-5xl max-h-[85vh] bg-site-bg/95 backdrop-blur-xl rounded-2xl border border-site-border shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Заголовок */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-site-border">
          <h2 className="text-2xl font-bold text-site-text">Сотрудники лаборатории</h2>
          <button
            onClick={onClose}
            className="text-site-muted hover:text-site-text transition-colors p-2 rounded-lg hover:bg-site-surface/50"
            aria-label="Закрыть"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Контент с прокруткой */}
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)]">
          {staff.length === 0 ? (
            <Card className="p-6 text-site-secondary text-center">
              Сотрудники пока не добавлены.
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {staff.map((m) => (
                <Card key={m.id} hover className="group overflow-hidden">
                  <div className="p-4 sm:p-5 flex gap-3">
                    <Link
                      prefetch={false}
                      href={`/users/${m.id}`}
                      aria-label={`Открыть профиль: ${m.display_name || m.username}`}
                      title="Открыть профиль"
                      className="relative w-[67px] h-[67px] sm:w-[84px] sm:h-[84px] rounded-full overflow-hidden border border-site-border-control/50 bg-site-action flex items-center justify-center text-site-on-accent font-bold shrink-0"
                      onClick={onClose}
                    >
                      {m.avatar_display_url ? (
                        <img
                          src={m.avatar_display_url}
                          alt={m.display_name || m.username}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        (m.display_name?.[0] || m.username?.[0] || '?').toUpperCase()
                      )}
                    </Link>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="text-lg font-semibold text-site-text truncate">
                            <Link 
                              prefetch={false} 
                              href={`/users/${m.id}`} 
                              className="hover:underline"
                              onClick={onClose}
                            >
                              {m.display_name || m.username}
                            </Link>
                          </h3>
                          <div className="text-sm text-site-muted truncate">@{(m as any).nickname || m.username}</div>
                        </div>

                        {m.role?.name && (
                          <Badge variant="custom" color={m.role.color || '#64748B'} size="sm">
                            {m.role.name}
                          </Badge>
                        )}
                      </div>

                      {(m.position || m.organization) && (
                        <div className="mt-1.5 text-sm text-site-accent">
                          {[m.position, m.organization].filter(Boolean).join(' • ')}
                        </div>
                      )}

                      {/* "Время работы": задаётся сотрудником в профиле (публично) */}
                      {(m.work_time || m.work_since) && (
                        <div className="mt-1.5 text-xs text-site-muted">
                          <UiIcon name="Clock" />{' '}
                          {m.work_time
                            ? m.work_time
                            : (m.work_since ? `с ${new Date(m.work_since).getFullYear()}` : '')}
                        </div>
                      )}

                      {/* Метрики: статьи/проекты (публичные) */}
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Badge variant="secondary" size="sm">
                          <UiIcon name="Article" /> {m.articles_count ?? 0}
                        </Badge>
                        <Badge variant="secondary" size="sm">
                          <UiIcon name="Rocket" /> {m.projects_count ?? (m.projects?.length ?? 0)}
                        </Badge>
                      </div>

                      {/* Проекты (если есть) */}
                      {m.projects && m.projects.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {m.projects.slice(0, 3).map((p) => (
                            <Link
                              key={p.id}
                              prefetch={false}
                              href={`/projects/${p.id}`}
                              className="text-xs text-site-text bg-site-surface/40 border border-site-border rounded-full px-2 py-1 hover:bg-site-surface/60 transition-colors truncate max-w-[180px]"
                              title={p.name}
                              onClick={onClose}
                            >
                              {p.name}
                            </Link>
                          ))}
                        </div>
                      )}

                      {m.bio && (
                        <p className="mt-2 text-sm text-site-secondary leading-relaxed line-clamp-3">
                          {m.bio}
                        </p>
                      )}

                      <div className="mt-3 flex items-center justify-between gap-3">
                        <div className="text-xs text-site-muted truncate">
                          {m.telegram_username ? `tg: @${m.telegram_username}` : ''}
                        </div>

                        <Button size="sm" variant="outline">
                          <Link
                            prefetch={false}
                            href={`/messages/${m.id}`}
                            aria-label={`Написать: ${m.display_name || m.username}`}
                            onClick={onClose}
                          >
                            Написать
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
